import React, { useState, useContext } from 'react';
import { AuthContext, API } from '../App';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { BookOpen, Eye, EyeOff } from 'lucide-react';

const Auth = () => {
  const { login } = useContext(AuthContext);
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showCustomField, setShowCustomField] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    field_of_concentration: '',
    school: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check terms agreement for registration
    if (!isLogin && !agreedToTerms) {
      toast.error('You must agree to the Terms of Use and Privacy Policy to create an account');
      return;
    }
    
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const response = await axios.post(`${API}${endpoint}`, formData);
      login(response.data.user, response.data.token);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotPasswordEmail) {
      toast.error('Please enter your email address');
      return;
    }
    
    setForgotPasswordLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotPasswordEmail });
      toast.success('If an account with that email exists, a password reset link has been sent.');
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 md:mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-blue-600 to-blue-800 rounded-2xl mb-3 md:mb-4 shadow-lg">
            <BookOpen className="w-7 h-7 md:w-8 md:h-8 text-blue-50" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
            DocMarket
          </h1>
          <p className="text-sm md:text-base text-gray-600">Your Academic Knowledge Marketplace</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-200">
          <div className="flex gap-2 mb-6">
            <Button
              data-testid="auth-toggle-login-btn"
              onClick={() => setIsLogin(true)}
              variant={isLogin ? 'default' : 'outline'}
              className="flex-1 rounded-xl"
            >
              Sign In
            </Button>
            <Button
              data-testid="auth-toggle-register-btn"
              onClick={() => setIsLogin(false)}
              variant={!isLogin ? 'default' : 'outline'}
              className="flex-1 rounded-xl"
            >
              Sign Up
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    data-testid="auth-name-input"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label htmlFor="school">School / Institution</Label>
                  <Input
                    id="school"
                    data-testid="auth-school-input"
                    type="text"
                    value={formData.school}
                    onChange={(e) => setFormData({ ...formData, school: e.target.value })}
                    className="mt-1 rounded-xl"
                    placeholder="e.g. Stanford University"
                  />
                </div>
                <div>
                  <Label htmlFor="field">Field of Study (Optional)</Label>
                  {!showCustomField ? (
                    <Select 
                      value={formData.field_of_concentration || 'none'}
                      onValueChange={(v) => {
                        if (v === 'custom') {
                          setShowCustomField(true);
                          setFormData({ ...formData, field_of_concentration: '' });
                        } else if (v === 'none') {
                          setFormData({ ...formData, field_of_concentration: '' });
                        } else {
                          setFormData({ ...formData, field_of_concentration: v });
                        }
                      }}
                    >
                      <SelectTrigger data-testid="auth-field-select" className="mt-1 rounded-xl">
                        <SelectValue placeholder="Select your field..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None / Skip</SelectItem>
                        <SelectItem value="Computer Science">Computer Science</SelectItem>
                        <SelectItem value="Biology">Biology</SelectItem>
                        <SelectItem value="Chemistry">Chemistry</SelectItem>
                        <SelectItem value="Physics">Physics</SelectItem>
                        <SelectItem value="Mathematics">Mathematics</SelectItem>
                        <SelectItem value="Engineering">Engineering</SelectItem>
                        <SelectItem value="Medicine">Medicine</SelectItem>
                        <SelectItem value="Business">Business</SelectItem>
                        <SelectItem value="Psychology">Psychology</SelectItem>
                        <SelectItem value="Literature">Literature</SelectItem>
                        <SelectItem value="History">History</SelectItem>
                        <SelectItem value="custom">+ Enter Custom Field</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="flex gap-2 mt-1">
                      <Input
                        data-testid="auth-field-custom-input"
                        type="text"
                        value={formData.field_of_concentration}
                        onChange={(e) => setFormData({ ...formData, field_of_concentration: e.target.value })}
                        placeholder="Enter your field of study..."
                        className="rounded-xl"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setShowCustomField(false);
                          setFormData({ ...formData, field_of_concentration: '' });
                        }}
                        className="rounded-xl whitespace-nowrap"
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                data-testid="auth-email-input"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  data-testid="auth-password-input"
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="mt-1 rounded-xl pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {isLogin && (
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-blue-600 hover:text-blue-800 mt-2 underline"
                >
                  Forgot password?
                </button>
              )}
            </div>
            
            {!isLogin && (
              <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-200">
                <Checkbox
                  id="terms"
                  data-testid="terms-checkbox"
                  checked={agreedToTerms}
                  onCheckedChange={setAgreedToTerms}
                  className="mt-1"
                />
                <label htmlFor="terms" className="text-sm text-gray-700 cursor-pointer">
                  I agree to the{' '}
                  <Link to="/terms" className="text-blue-600 hover:text-blue-800 underline font-medium" target="_blank">
                    Terms of Use
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-blue-600 hover:text-blue-800 underline font-medium" target="_blank">
                    Privacy Policy
                  </Link>
                </label>
              </div>
            )}
            
            <Button
              data-testid="auth-submit-btn"
              type="submit"
              className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900"
            >
              {isLogin ? 'Sign In' : 'Create Account'}
            </Button>
          </form>
        </div>

        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8 w-full max-w-md">
              <h2 className="text-2xl font-bold text-gray-800 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>
                Reset Password
              </h2>
              <p className="text-gray-600 mb-6">
                Enter your email address and we'll send you a link to reset your password.
              </p>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div>
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    required
                    value={forgotPasswordEmail}
                    onChange={(e) => setForgotPasswordEmail(e.target.value)}
                    className="mt-1 rounded-xl"
                    placeholder="Enter your email"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowForgotPassword(false);
                      setForgotPasswordEmail('');
                    }}
                    className="flex-1 rounded-xl"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={forgotPasswordLoading}
                    className="flex-1 rounded-xl bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900"
                  >
                    {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Auth;
